import axios from 'axios';
import type { SpatialGrid, H3GridFeatureCollection, GridStyle, CreateGridDto } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// Log API_URL once on module initialization for diagnostics
console.log('[grid-api] API_URL:', API_URL);

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Cliente API para mallas H3
 *
 * @remarks
 * Proporciona métodos para interactuar con el backend de mallas H3.
 * Todos los métodos incluyen automáticamente el token JWT si está disponible.
 *
 * @example
 * ```typescript
 * import { gridApi } from '@/core/spatial';
 *
 * // Crear malla
 * const grid = await gridApi.create(projectId, {
 *   name: 'Malla Base',
 *   resolution: 8
 * });
 *
 * // Obtener GeoJSON
 * const geojson = await gridApi.getGeoJSON(grid.id);
 * ```
 */
export const gridApi = {
  /**
   * Crear malla H3 para un proyecto
   *
   * @param projectId - UUID del proyecto
   * @param data - Datos de la malla (name, resolution, description?)
   * @returns Malla H3 creada
   */
  async create(projectId: string, data: CreateGridDto): Promise<SpatialGrid> {
    const token = getAuthToken();
    const response = await axios.post<SpatialGrid>(
      `${API_URL}/projects/${projectId}/grids`,
      data,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Listar mallas de un proyecto
   *
   * @param projectId - UUID del proyecto
   * @returns Array de mallas ordenadas por resolución
   */
  async list(projectId: string): Promise<SpatialGrid[]> {
    const token = getAuthToken();
    const response = await axios.get<SpatialGrid[]>(
      `${API_URL}/projects/${projectId}/grids`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Obtener malla específica
   *
   * @param gridId - UUID de la malla
   * @returns Malla H3 con datos completos
   */
  async get(gridId: string): Promise<SpatialGrid> {
    const token = getAuthToken();
    const response = await axios.get<SpatialGrid>(`${API_URL}/grids/${gridId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Obtener GeoJSON FeatureCollection de una malla
   *
   * @param gridId - UUID de la malla
   * @returns GeoJSON listo para MapLibre GL
   *
   * @example
   * ```typescript
   * const geojson = await gridApi.getGeoJSON(gridId);
   * mapEngine.addLayer({
   *   id: 'h3-grid',
   *   type: 'fill',
   *   source: { type: 'geojson', data: geojson }
   * });
   * ```
   */
  async getGeoJSON(gridId: string): Promise<H3GridFeatureCollection> {
    const token = getAuthToken();
    const response = await axios.get<H3GridFeatureCollection>(
      `${API_URL}/grids/${gridId}/geojson`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Actualizar nombre y/o estilos de una malla
   *
   * @param gridId - UUID de la malla
   * @param data - Campos a actualizar (name?, style?)
   * @returns Malla actualizada
   */
  async update(gridId: string, data: { name?: string; style?: GridStyle }): Promise<SpatialGrid> {
    const token = getAuthToken();
    const response = await axios.patch<SpatialGrid>(
      `${API_URL}/grids/${gridId}`,
      data,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /** @deprecated Usar update({ name }) */
  async updateName(gridId: string, name: string): Promise<SpatialGrid> {
    return gridApi.update(gridId, { name });
  },

  /**
   * Asigna o elimina el nombre personalizado de una celda.
   * @param gridId  - UUID de la malla
   * @param h3Index - H3 index de la celda
   * @param name    - Nuevo nombre (string vacío para eliminar)
   * @returns Malla actualizada (con cellNames actualizado)
   */
  async setCellName(gridId: string, h3Index: string, name: string): Promise<SpatialGrid> {
    const token = getAuthToken();
    const response = await axios.patch<SpatialGrid>(
      `${API_URL}/grids/${gridId}/cells/${h3Index}/name`,
      { name },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  },

  /**
   * Eliminar malla
   *
   * @param gridId - UUID de la malla a eliminar
   */
  async delete(gridId: string): Promise<void> {
    const token = getAuthToken();
    await axios.delete(`${API_URL}/grids/${gridId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
};
